import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as BlackStarsIcon } from 'assets/icons/BlackStars.svg';
import { ReactComponent as StarsIcon } from 'assets/icons/BlackStars.svg';
import { useMemo, useState } from 'react';
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon';
import { useProjectRetroContext } from '../../context/ProjectRetroContext';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { keyBy, mapValues, uniqBy } from 'lodash';

function SummarizeLearnings({ lessons }: { lessons: ArticleType[] }) {
  const { companyId } = useUserData();
  const { entries } = useConfigQuery({
    companyId,
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });
  const {
    state: { isOpenedLessonForm },
    dispatch,
  } = useProjectRetroContext();
  const theme = useTheme();
  const categories = useMemo(() => {
    const phasesAndMilestones: Record<
      string,
      { entryCode: string; entryValues: { label: string; value: string }[] }
    > = {};
    const entriesByStatus = mapValues(keyBy(entries, 'entryCode'), 'isActive');
    lessons.forEach((lesson) => {
      (lesson.projectPhases || []).forEach((phase) => {
        if (entriesByStatus[phase] === undefined || entriesByStatus[phase]) {
          phasesAndMilestones[phase] = {
            ...phasesAndMilestones[phase],
            entryCode: phase,
            entryValues: uniqBy(
              [
                ...(phasesAndMilestones[phase]?.entryValues || []),
                ...(lesson.projectMileStones || []).map((entryValue) => ({
                  label: entryValue,
                  value: entryValue,
                })),
              ],
              'value',
            ),
          };
        }
      });
    });

    return Object.values(phasesAndMilestones);
  }, [entries, lessons]);

  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const isOptionSelected = useMemo(() => {
    return Object.values(selected).some((arr) => (arr && arr.length === 0) || arr?.length > 0);
  }, [selected]);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const handleCheckboxChange = (categoryCode, value) => {
    setSelected((prev) => {
      const selectedItems = prev[categoryCode] || [];
      if (selectedItems.includes(value)) {
        return {
          ...prev,
          [categoryCode]: selectedItems.filter((item) => item !== value),
        };
      } else {
        return {
          ...prev,
          [categoryCode]: [...selectedItems, value],
        };
      }
    });
  };

  const handleSelectAll = () => {
    setSelected((prev) => {
      const newSelected = {};
      categories.forEach((category) => {
        newSelected[category.entryCode] = category.entryValues.map((item) => item.value);
      });
      return categories.length === Object.keys(prev).length ? [] : newSelected;
    });
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClick = (event) => {
    if (isOpenedLessonForm) {
      dispatch({
        payload: {
          articleIds: [],
          isOpenedLessonForm: false,
        },
      });
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSelected({});
    setExpanded(null);
  };

  const handleSumarize = () => {
    const articleIds = lessons
      ?.filter((article) => {
        return article?.projectPhases?.some(
          (phase) =>
            (selected[phase] && selected[phase].length === 0) ||
            article?.projectMileStones?.some((milestone) => selected[phase]?.includes(milestone)),
        );
      })
      .map((article) => article.id);
    handleClose();
    dispatch({
      payload: {
        articleIds,
        isOpenedLessonForm: articleIds.length > 0,
      },
    });
  };

  return (
    <>
      <Button
        onClick={handleClick}
        startIcon={<StarsIcon width={18} height={18} />}
        sx={(theme) => ({
          color: theme.palette.custom.link,
          '& svg path': { fill: theme.palette.custom.link },
        })}
      >
        Summarize Learnings
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          mt: 1,
          '& .MuiPopover-paper': {
            minWidth: 400,
            boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
          },
        }}
      >
        <Box sx={{ padding: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography
              sx={(theme) => ({ fontWeight: 700, color: theme.palette.custom.activeTitle })}
            >
              Select Milestones
            </Typography>
            <Button
              onClick={handleSelectAll}
              variant="text"
              sx={(theme) => ({ color: theme.palette.custom.link, ml: 8 })}
            >
              {categories.length === Object.keys(selected).length ? 'Unselect All' : 'Select All'}
            </Button>
          </Box>

          {categories.map((category) => {
            return (
              <Accordion
                key={category.entryCode}
                expanded={expanded === category.entryCode}
                onChange={handleAccordionChange(category.entryCode)}
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  expandIcon={<ArrowDownSLineIcon color={theme.palette.custom.navy} />}
                  sx={{
                    px: 0,
                    height: '30px',
                    '&.Mui-expanded': {
                      minHeight: 10,
                    },
                  }}
                >
                  <FormControlLabel
                    sx={(theme) => ({ color: theme.palette.custom.activeTitle })}
                    control={
                      <Checkbox
                        checked={
                          (selected[category.entryCode] &&
                            selected[category.entryCode].length === 0) ||
                          (selected[category.entryCode]?.length === category.entryValues.length &&
                            category.entryValues.length > 0)
                        }
                        indeterminate={
                          selected[category.entryCode]?.length > 0 &&
                          selected[category.entryCode]?.length < category.entryValues.length
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelected((prev) => {
                            const prevState = { ...prev };

                            if (checked) {
                              prevState[category.entryCode] = category.entryValues.map(
                                (item) => item.value,
                              );
                            } else {
                              delete prevState[category.entryCode];
                            }

                            return prevState;
                          });
                        }}
                      />
                    }
                    label={<Typography>{category.entryCode}</Typography>}
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                {category.entryValues.length > 0 && (
                  <AccordionDetails
                    sx={(theme) => ({
                      borderLeft: `1px solid ${theme.palette.custom.lightGray}`,
                      ml: 1.25,
                    })}
                  >
                    <FormGroup>
                      {category.entryValues.map((item) => (
                        <FormControlLabel
                          key={item.value}
                          sx={(theme) => ({
                            color: theme.palette.custom.activeTitle,
                            fontWeight: 200,
                          })}
                          control={
                            <Checkbox
                              checked={selected[category.entryCode]?.includes(item.value) || false}
                              onChange={() => handleCheckboxChange(category.entryCode, item.value)}
                            />
                          }
                          label={item.label}
                        />
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                )}
              </Accordion>
            );
          })}
          <Button
            variant="contained"
            sx={{ mt: 2, mb: 2, float: 'right' }}
            onClick={handleSumarize}
            startIcon={<BlackStarsIcon width={18} height={18} />}
            disabled={!isOptionSelected}
          >
            Summarize
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default SummarizeLearnings;
